const rootSelector = "[data-select]";
const openClass = "is-open";

let initialized = false;
let activeRoot = null;

export function initCustomSelects() {
  if (initialized) return;

  const roots = [...document.querySelectorAll(rootSelector)];
  if (!roots.length) return;

  initialized = true;
  roots.forEach(initSelect);
  bindGlobalClose();
  bindFormSubmits();
  bindSubmitSanitizer();
}

function initSelect(root) {
  const isSingle = root.getAttribute("data-select-mode") === "single";
  const toggle = root.querySelector("[data-select-toggle]");
  const menu = root.querySelector("[data-select-menu]");
  const pills = root.querySelector("[data-select-pills]");
  const placeholder = root.querySelector("[data-select-placeholder]");
  const hidden = root.querySelector("[data-select-hidden]");
  const nativeSelect = getNativeSelect(root);

  if (!menu) return;

  buildMenu(root, menu, nativeSelect);
  syncFromNative(menu, nativeSelect);
  render();

  toggle?.setAttribute("aria-expanded", "false");

  menu.addEventListener("change", (event) => {
    const input = event.target;
    if (!isOptionInput(input)) return;

    if (isSingle && input.checked) {
      getOptions(menu).forEach((option) => {
        if (option !== input) option.checked = false;
      });
      close(root);
    }

    render();
  });

  function render() {
    const selected = getOptions(menu)
      .filter((input) => input.checked)
      .map((input) => ({
        input,
        value: getValue(input, nativeSelect),
        label: getLabel(input)
      }));
    const values = selected.map((item) => item.value);

    renderPills(pills, selected, render, isSingle);
    renderPlaceholder(placeholder, selected, nativeSelect, isSingle);
    setNativeValue(nativeSelect, values);
    setFieldValue(hidden, values.join(", "));
  }
}

function getNativeSelect(root) {
  return root.querySelector("select[data-select-native]") ||
    root.closest("[data-select-field], .form__input-wrap--select, .form__input-wrap")
      ?.querySelector("select[data-select-native]");
}

function buildMenu(root, menu, nativeSelect) {
  if (!nativeSelect || menu.children.length) return;

  [...nativeSelect.options].forEach((option) => {
    if (!option.value) return;

    const label = document.createElement("label");
    const text = document.createElement("span");
    const input = document.createElement("input");

    label.className = root.getAttribute("data-select-option-class") || "";
    text.className = root.getAttribute("data-select-option-text-class") || "";
    input.className = root.getAttribute("data-select-input-class") || "";
    label.setAttribute("data-select-option", "");
    text.setAttribute("data-select-option-text", "");
    text.textContent = option.textContent.trim();
    input.type = "checkbox";
    input.value = option.value;

    label.appendChild(text);
    label.appendChild(input);
    menu.appendChild(label);
  });
}

function syncFromNative(menu, nativeSelect) {
  if (!nativeSelect) return;

  const values = [...nativeSelect.options]
    .filter((option) => option.selected && option.value)
    .map((option) => option.value);

  getOptions(menu).forEach((option) => {
    option.checked = values.includes(option.value);
  });
}

function getOptions(menu) {
  return [...menu.querySelectorAll('input[type="checkbox"], input[type="radio"]')];
}

function getLabel(input) {
  return (input.closest("label")?.querySelector("[data-select-option-text]")?.textContent || input.value).trim();
}

function getValue(input, nativeSelect) {
  const label = getLabel(input);
  const candidates = [input.value, label].map(normalize);
  const matchedOption = nativeSelect ? [...nativeSelect.options].find((option) => {
    return candidates.includes(normalize(option.value)) ||
      candidates.includes(normalize(option.textContent));
  }) : null;

  return matchedOption ? matchedOption.value : input.value;
}

function renderPills(pills, selected, render, isSingle) {
  if (!pills) return;

  pills.innerHTML = "";
  if (isSingle) return;

  selected.forEach((item) => {
    const pill = document.createElement("button");

    pill.type = "button";
    pill.className = "select__pill";
    pill.textContent = item.label + " x";
    pill.addEventListener("click", (event) => {
      event.stopPropagation();
      item.input.checked = false;
      render();
    });
    pills.appendChild(pill);
  });
}

function renderPlaceholder(placeholder, selected, nativeSelect, isSingle) {
  if (!placeholder) return;

  const emptyText = nativeSelect?.querySelector('option[value=""]')?.textContent.trim() || "Select an option...";

  if (isSingle) {
    placeholder.textContent = selected[0]?.label || emptyText;
    return;
  }

  placeholder.style.display = selected.length ? "none" : "";
  placeholder.textContent = emptyText;
}

function setNativeValue(nativeSelect, values) {
  if (!nativeSelect) return;

  if (nativeSelect.multiple) {
    [...nativeSelect.options].forEach((option) => {
      option.selected = values.includes(option.value);
    });
  } else {
    nativeSelect.value = values[0] || "";
  }

  dispatch(nativeSelect);
}

function setFieldValue(field, value) {
  if (!field) return;

  field.value = value;
  dispatch(field);
}

function open(root) {
  if (activeRoot && activeRoot !== root) close(activeRoot);

  activeRoot = root;
  root.classList.add(openClass);
  root.querySelector("[data-select-toggle]")?.setAttribute("aria-expanded", "true");
}

function close(root) {
  root.classList.remove(openClass);
  root.querySelector("[data-select-toggle]")?.setAttribute("aria-expanded", "false");

  if (activeRoot === root) activeRoot = null;
}

function bindGlobalClose() {
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-select-toggle]");

    if (toggle) {
      const root = toggle.closest("[data-select]");

      event.preventDefault();

      if (root) root.classList.contains(openClass) ? close(root) : open(root);
      return;
    }

    if (activeRoot && !activeRoot.contains(event.target)) close(activeRoot);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && activeRoot) close(activeRoot);
  });
}

function bindFormSubmits() {
  document.querySelectorAll("[data-form-submit]").forEach((button) => {
    button.addEventListener("click", (event) => {
      const form = button.closest("form");

      event.preventDefault();

      if (form?.requestSubmit) {
        form.requestSubmit();
      } else {
        form?.querySelector('input[type="submit"]')?.click();
      }
    });
  });
}

function bindSubmitSanitizer() {
  document.querySelectorAll("form").forEach((form) => {
    form.addEventListener("submit", () => {
      disableUiInputsForSubmit(form);
    }, true);
  });
}

function disableUiInputsForSubmit(form) {
  const inputs = [...form.querySelectorAll("[data-select-menu] input")];

  inputs.forEach((input) => {
    input.disabled = true;
  });

  setTimeout(() => {
    inputs.forEach((input) => {
      input.disabled = false;
    });
  }, 1000);
}

function isOptionInput(input) {
  return input instanceof HTMLInputElement && (input.type === "checkbox" || input.type === "radio");
}

function normalize(value) {
  return String(value || "").trim().toLowerCase().replace(/[-_\s]+/g, " ");
}

function dispatch(element) {
  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
}
